import React, { useMemo, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  HelpCircle, Search, Clock, Table2, CalendarDays, CheckSquare, Wrench, Package,
  Plane, Landmark, Truck, Users, Bell, Settings, Sun, ShieldCheck, ZoomIn, PlayCircle,
} from "lucide-react";

const SECTIONS = [
  {
    id: "getting-started",
    title: "Getting Started & Your Account",
    icon: ShieldCheck,
    items: [
      { q: "How do I log in?", a: "Use your username (first initial + last name, e.g. jsmith) and the password given to you by your admin. If you forget your password, ask a System Admin to reset it from Admin Settings → Users." },
      { q: "Can I install this as an app on my phone or computer?", a: "Yes — this is a PWA (Progressive Web App). On desktop Chrome/Edge, click the Install button in the top navigation bar. On iPhone, open the site in Safari → Share → Add to Home Screen. On Android, Chrome will prompt you or use Menu → Install app. Once installed it works like a native app, including offline support." },
      { q: "Does the app work offline?", a: "Partially. If you lose connection while clocking in/out, your action is queued on your device and automatically synced to the server when you're back online. You'll see a banner when actions are pending sync." },
      { q: "How do I update my profile or change my password?", a: "Click your name in the top-right corner → Profile. There you can view your details, manage push notifications, and change your password. Some fields (role, job title, manager) can only be changed by an admin." },
      { q: "What are the different roles?", a: "System Admin — full access to everything including Admin Settings. User Admin — manages users, approvals and most modules. Employee (Engineer) — clocks in/out, submits timesheets, uses day-to-day modules. Some pages are further restricted by job title (Supervisor, Manager, Senior Manager)." },
    ],
  },
  {
    id: "clocking",
    title: "Clocking In & Duty Tracking",
    icon: Clock,
    items: [
      { q: "How do I clock in to an aircraft?", a: "From the Clock In page, pick the aircraft tail, choose a task type, and hit Clock In. Location must be turned on — the app records where you clocked in. When finished, clock out and your hours are added to your timesheet automatically.", img: "/faq/clock-in.png", imgCaption: "Press the Clock In button on any aircraft card to start recording hours against that tail." },
      { q: "Why does the app need my location?", a: "Clock-ins are geofenced to your base. If you clock in away from base, the entry is still allowed but flagged, and the schedule shows a small marker with the nearest city. Location is only captured at clock-in/clock-out and duty start — it is not tracked continuously (except 30-minute pings while on duty)." },
      { q: "What is Duty tracking and how is it different from clocking in?", a: "Duty marks your overall on-shift window (start of duty to end of duty), while aircraft clock-ins record hands-on time against a specific tail. Start duty when your shift begins; clock into aircraft as you work on them. Duty sessions auto-close if you forget to end them.", img: "/faq/duty.png", imgCaption: "The Start duty button lives on your dashboard next to the timer." },
      { q: "What happens if I forget to clock out?", a: "Sessions are auto-closed by the system after your expected hours plus a grace period (e.g. 8-hour users are auto-clocked-out at 9 hours, recording 8). The entry is flagged so your approver can review and correct it if needed." },
      { q: "What timezone are times recorded in?", a: "Everything is recorded and displayed in MST (Mountain Time / America-Denver), regardless of where you physically are." },
    ],
  },
  {
    id: "timesheets",
    title: "Timesheets & Pay Periods",
    icon: Table2,
    items: [
      { q: "Where do I see my hours?", a: "Click your name in the top-right → My Timesheet. It shows every day of the current pay period with hours, task codes, per diems and totals. Use the period selector to look at previous pay periods." },
      { q: "How do pay periods work?", a: "Pay periods are fixed two-week windows numbered through the year. The schedule and timesheet pages both let you flip between Month view and Pay Period view." },
      { q: "How do I submit my timesheet for approval?", a: "At the end of a pay period, open My Timesheet and press Submit. It goes to your approver (L1), and once approved may go to a second-level approver (L2). You'll get a notification when it's approved or returned.", img: "/faq/timesheet-submit.png", imgCaption: "The Submit for approval button on your timesheet page." },
      { q: "What are per diems and how do I claim one?", a: "Per diems (AL = full day, AM = half day) are daily allowances for working away from base. They're added on the timesheet as flags on a day rather than hours. Your approver can also add or correct them during review." },
      { q: "How do stat holidays work?", a: "Stat holidays are configured per location by admins. On a stat day the schedule shows H, and the timesheet applies your location's holiday rules automatically. If you actually work a stat day, clock in as normal and the correct premium treatment is applied by your approver." },
      { q: "Can my manager edit my timesheet?", a: "Approvers can adjust entries during review (fix a missed clock-out, add a per diem, correct a task code). Every manual edit is recorded with who made it." },
    ],
  },
  {
    id: "schedule",
    title: "Schedule & Travel",
    icon: CalendarDays,
    items: [
      { q: "What do the schedule letters mean?", a: "W = Working, O = Off (projected from your shift pattern), V = Vacation, S = Sick, DOF = Day Off, H = Stat Holiday. The legend above the grid shows the colors for each.", img: "/faq/schedule-legend.png", imgCaption: "The legend sits directly above the schedule grid." },
      { q: "What is the solid blue block with a plane icon?", a: "That's a Travel day — an admin has marked that person as travelling (flights, hotel, etc.). Hover over the cell to read the travel details and who set them.", img: "/faq/schedule-travel.png", imgCaption: "A travel day fills the whole cell in blue with a plane icon and TRVL label." },
      { q: "How do I add travel details to someone's schedule?", a: "System Admins and User Admins: open Team → Schedule, then click any day cell on a user's row. A dialog opens where you can add, edit or remove the travel note. The user is notified automatically when travel is added to their schedule.", img: "/faq/travel-dialog.png", imgCaption: "Clicking a day cell opens this dialog — type the travel details and hit Save." },
      { q: "What is the small amber tag on some schedule cells?", a: "It means that person clocked in away from their base that day — the tag shows the nearest city code, and hovering shows the distance from base." },
      { q: "Who can see the Schedule page?", a: "Supervisors, Managers, Senior Managers and Admins. It groups people by city → department → reporting chain, and cities can be collapsed by clicking their header." },
    ],
  },
  {
    id: "approvals",
    title: "Approvals & Delegation",
    icon: CheckSquare,
    items: [
      { q: "How do timesheet approvals work?", a: "When an employee submits a timesheet, their L1 approver gets a notification and reviews it under Team → Timesheets. Depending on setup, an L2 approver signs off after L1. Approvers can approve, or return the sheet with comments for correction." },
      { q: "I'm going on vacation — how do I hand off my approvals?", a: "Use Out-of-Office delegation from your Profile. Pick a delegate and a date range; while active, your approvals and tasks route to the delegate automatically, and everything reverts when the window ends." },
      { q: "How do I know something is waiting for me?", a: "The bell icon in the top bar shows in-app notifications, red count badges appear on the Team/Tasks menus, and if you enabled push notifications you'll get them on your device even when the app is closed." },
    ],
  },
  {
    id: "tasks-repairs",
    title: "Tasks & Repair Estimate Approvals",
    icon: Wrench,
    items: [
      { q: "What lives on the Tasks page?", a: "Action items assigned to you — including repair estimate approvals routed to you as an approver. Items show due-date countdowns and get badges when new." },
      { q: "How do I request a repair estimate approval?", a: "Open Tasks → Repair Estimates and create a request: describe the repair, add the cost (CAD, USD, GBP or EUR), due date and the approver. The approver is notified and can Approve, Deny, or send it back as Revisit with comments." },
      { q: "What does 'Revisit' mean on a repair estimate?", a: "The approver wants changes or more information before deciding. Edit the request and resend it — the revisit counter shows how long it's been waiting." },
      { q: "What happens to actioned repair requests?", a: "They move to the Actioned tab, keep a NEW badge until you open them, and are automatically deleted after their retention window (30–60 days depending on state)." },
    ],
  },
  {
    id: "stores",
    title: "Stores — Spares, Parts & Purchase Orders",
    icon: Package,
    items: [
      { q: "What is Spares Monitoring / Critical Spares?", a: "A live list of critical spare parts tracked across the fleet. Each part shows quantity, location and check history. Stores staff run periodic checks; observations (with photos/files) can be logged against any part." },
      { q: "How do I request a part?", a: "Use Stores → Parts Requests. Fill in the part, quantity and aircraft; the stores team is notified and tracks the request through to fulfilment." },
      { q: "What is the PO Overview?", a: "A management view of purchase orders — status, vendor, amounts and attachments — so open POs can be chased and closed out. Access is controlled by admins under Stores Access." },
      { q: "Who gets access to Stores pages?", a: "Access is granted per-user by a System Admin in Admin Settings (Stores Access tab), separate from roles — so any user can be given Stores, PO or Heavy Check access as needed." },
    ],
  },
  {
    id: "fleet",
    title: "Fleet — Status, Planning, Maintenance & Heavy Checks",
    icon: Plane,
    items: [
      { q: "What is the Fleet Status page?", a: "The live board of every aircraft: serviceability, current status (including CAPEX codes), notes and who's working on it. Admins keep persistent notes per tail." },
      { q: "What is the Fleet Board?", a: "A full-screen, auto-refreshing display designed for hangar TVs (open /fleet/board). It shows fleet status at a glance and updates live over WebSockets — no login required on the TV." },
      { q: "How does Fleet Planning work?", a: "Planning holds upcoming maintenance jobs per aircraft, imported from an Excel scan or added manually. Jobs show hours/cycles/days remaining (calculated from the aircraft's current log figures) and sort closest-due first. Overdue jobs get a pulsing red badge, also visible in the side navigation." },
      { q: "How do jobs move from Planning to Maintenance?", a: "When a job is ready to be worked, push it to Maintenance from the Planning page — it must have jobcard info first. Jobs can also be pulled back to Planning if plans change." },
      { q: "What is the tolerance toggle on Planning/Maintenance jobs?", a: "Tolerance lets you extend a job's due point by extra hours/cycles/days (within approved limits). Applying tolerance records who applied it and when — hover the toggle to see." },
      { q: "What is Heavy Check Status?", a: "A live tracker for heavy checks (C/D checks): jobcards with statuses, NRC split, parts with currency breakdown (CAD/USD/GBP), progress bars, and Excel/PDF exports. You can re-scan an updated jobcard file and it diffs added/removed cards without wiping manual statuses. Access is granted per-user; you can star a favourite check to land on it by default." },
      { q: "How does aircraft maintenance auto-archive work?", a: "Completed work-log entries older than 3 weeks are archived automatically to keep the active list clean. Archived items remain viewable in the archive." },
    ],
  },
  {
    id: "capital",
    title: "Capital Requests",
    icon: Landmark,
    items: [
      { q: "What is the Capital page for?", a: "Submitting and approving capital expenditure requests. A request moves through approval levels (L1 → L2 → L3); each approver is notified in turn and the requester sees status updates." },
      { q: "Can I attach files to a capital request?", a: "Yes — quotes, photos and supporting documents can be attached when creating or editing the request." },
    ],
  },
  {
    id: "gse",
    title: "Ground Support Equipment (GSE)",
    icon: Truck,
    items: [
      { q: "What is tracked in GSE?", a: "Every piece of ground support equipment — with a photo, details, service status and work orders. Click a photo to enlarge it." },
      { q: "How do GSE work orders function?", a: "Open a piece of equipment and create a work order describing the issue. Work orders track through completion, keeping a service history per unit." },
      { q: "What is the GSE QR scan?", a: "Each unit has a QR code; scanning it with a phone opens that unit's page directly, so issues can be logged right at the machine." },
    ],
  },
  {
    id: "team",
    title: "Team — Announcements, Discussion & Reviews",
    icon: Users,
    items: [
      { q: "Where are company announcements?", a: "Team → Announcements. Admins post there; important updates may also arrive as notifications." },
      { q: "What is the Discussion page?", a: "A team forum — post topics (optionally tagged to an aircraft), comment, attach photos, and pin important threads. You're notified when someone replies to your topic." },
      { q: "How do performance reviews work?", a: "Under Reviews (user menu), you can see reviews about you and, if you're a manager, write reviews for your reports using the configured rating categories." },
      { q: "What is the On-Duty page?", a: "A live roster of who's currently on duty, grouped by city, with each person's last known location fix and shift log events (duty starts/stops, out-of-range clock-ins)." },
      { q: "Where do I find everyone's vacation?", a: "Team → On Vacation lists current and upcoming vacation. Admins and Senior Managers also see a daily vacation headcount banner on the Team page." },
    ],
  },
  {
    id: "notifications",
    title: "Notifications & the Bell",
    icon: Bell,
    items: [
      { q: "How do I turn on push notifications?", a: "Go to Profile and enable push notifications — your browser will ask for permission. Once on, you get approval alerts, travel updates, task assignments and more, even with the app closed. Each device (phone, laptop) needs to be enabled separately." },
      { q: "What shows up in the bell?", a: "Everything relevant to you: timesheet approvals, repair estimate decisions, capital approvals, discussion replies, travel added to your schedule, and system announcements. Click an item to jump to the related page; items can be marked read.", img: "/faq/bell.png", imgCaption: "The bell lives in the top navigation bar — a red badge shows unread count." },
      { q: "I'm not receiving push notifications — what do I check?", a: "1) Push is enabled on Profile for this device. 2) Your browser/OS allows notifications for this site (check system settings, especially on iPhone where the app must be installed to Home Screen first). 3) You're not in Do Not Disturb / Focus mode." },
    ],
  },
  {
    id: "admin",
    title: "Admin Settings (Admins only)",
    icon: Settings,
    items: [
      { q: "Where do I manage users?", a: "Admin Settings → Users: create accounts, set role, job title, manager, department, city, employment type and shift schedule; reset passwords; archive users who leave." },
      { q: "How do shift schedules drive the Schedule page?", a: "Each user is assigned a shift pattern (e.g. 5-on/2-off). The Schedule page projects W/O days from that pattern, then layers vacation, sick days, stat holidays and travel on top." },
      { q: "How do backups work?", a: "Admin Settings → Backup: download a full JSON snapshot of every collection anytime, and the system also auto-saves a weekly backup (Sundays 3AM MST, 8 weeks retained). The snapshot preview shows document counts AND data size per collection so you can spot what's growing. Restore supports Merge (safe upsert) or Replace (wipe-and-load — irreversible)." },
      { q: "What else is configured in Admin Settings?", a: "Aircraft and templates, cities and geofences, departments, task types, stat holidays, CAPEX codes, timesheet templates, review categories, announcement posting, and per-user access grants for Stores / PO / Heavy Check pages." },
      { q: "What are stat holiday workflows?", a: "Admins define stat holidays per location. The system marks them on schedules, applies clock-in gating rules on those days, and feeds the correct premiums into timesheet approval." },
    ],
  },
  {
    id: "timeoff",
    title: "Time Off & Vacation",
    icon: Sun,
    items: [
      { q: "How is vacation or sick time recorded?", a: "Time off is entered as vacation, sick or day-off entries (by you or your approver depending on your setup). It appears on your timesheet, on the Schedule as V / S / DOF, and on the On-Vacation roster." },
      { q: "Do part-time employees show a schedule?", a: "Part-time users (PT badge) default to Off on the schedule; any day they actually log hours automatically shows as Working." },
    ],
  },
];

const VIDEO_GUIDES = [
  {
    id: "timesheets",
    title: "Timesheets — a quick tour",
    desc: "How to open your timesheet, read the pay-period grid, export it, and submit it for approval.",
    src: "/faq/videos/timesheets-tour.mp4",
    icon: Table2,
  },
  {
    id: "maintenance",
    title: "Maintenance — a quick tour",
    desc: "Changing aircraft status & details — MEL, Unserviceable with AOG parts on order, closing U/S items (MEL revert), clearing, and completing jobs.",
    src: "/faq/videos/maintenance-tour.mp4",
    icon: Wrench,
  },
];

function Highlight({ text, term }) {
  if (!term) return text;
  const i = text.toLowerCase().indexOf(term.toLowerCase());
  if (i === -1) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="bg-amber-200 rounded-sm px-0.5">{text.slice(i, i + term.length)}</mark>
      {text.slice(i + term.length)}
    </>
  );
}

export default function FAQ() {
  const [search, setSearch] = useState("");
  const [lightbox, setLightbox] = useState(null); // {src, caption}

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return SECTIONS;
    return SECTIONS.map((s) => ({
      ...s,
      items: s.items.filter((it) => it.q.toLowerCase().includes(term) || it.a.toLowerCase().includes(term)),
    })).filter((s) => s.items.length > 0);
  }, [search]);

  const totalQ = useMemo(() => SECTIONS.reduce((n, s) => n + s.items.length, 0), []);
  const shownQ = filtered.reduce((n, s) => n + s.items.length, 0);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10" data-testid="faq-page">
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-widest text-gray-500">Help Center</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-summit-ink flex items-center gap-3" data-testid="faq-title">
          <HelpCircle className="w-8 h-8 text-summit-primary" />
          Frequently Asked Questions
        </h1>
        <p className="text-sm text-gray-500 mt-2 max-w-2xl">
          New here? This page walks through every part of the system — from clocking in on an aircraft to
          fleet planning, approvals and admin tools. Use the search box to jump straight to a topic.
        </p>
      </div>

      <div className="relative mb-6">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search the FAQ… e.g. clock in, travel, backup, per diem"
          className="pl-9"
          data-testid="faq-search-input"
        />
      </div>

      {search.trim() && (
        <div className="mb-4 text-xs text-gray-500 font-mono" data-testid="faq-search-count">
          {shownQ} of {totalQ} answers match “{search.trim()}”
        </div>
      )}

      {!search.trim() && (
        <div className="mb-8" data-testid="faq-video-guides">
          <div className="flex items-center gap-2 mb-3">
            <PlayCircle className="w-4 h-4 text-summit-primary" />
            <h2 className="font-display text-base font-medium tracking-tight text-summit-ink">Video guides</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {VIDEO_GUIDES.map((v) => (
              <div key={v.id} className="border border-summit-hairline rounded-lg bg-white overflow-hidden" data-testid={`faq-video-${v.id}`}>
                <video
                  controls
                  preload="metadata"
                  playsInline
                  className="w-full aspect-video bg-summit-ink"
                  data-testid={`faq-video-player-${v.id}`}
                >
                  <source src={v.src} type="video/mp4" />
                  <source src={v.src.replace(".mp4", ".webm")} type="video/webm" />
                </video>
                <div className="px-4 py-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-summit-ink">
                    <v.icon className="w-3.5 h-3.5 text-summit-primary" />
                    {v.title}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {filtered.length === 0 && (
        <div className="p-6 text-sm text-gray-500 border border-summit-hairline rounded-md bg-white" data-testid="faq-no-results">
          No answers match your search. Try a different keyword — or ask your admin.
        </div>
      )}

      <div className="space-y-5">
        {filtered.map((section) => (
          <div key={section.id} className="border border-summit-hairline rounded-lg bg-white overflow-hidden" data-testid={`faq-section-${section.id}`}>
            <div className="px-5 py-3 bg-summit-glacier/50 border-b border-summit-hairline flex items-center gap-2">
              <section.icon className="w-4 h-4 text-summit-primary" />
              <h2 className="font-display text-base font-medium tracking-tight text-summit-ink">{section.title}</h2>
              <span className="ml-auto font-mono text-[10px] text-gray-400">{section.items.length} Q</span>
            </div>
            <Accordion key={search.trim() || "idle"} type="multiple" className="px-5" defaultValue={search.trim() ? section.items.map((_, i) => `${section.id}-${i}`) : []}>
              {section.items.map((it, i) => (
                <AccordionItem key={`${section.id}-${i}`} value={`${section.id}-${i}`} className="border-summit-hairline">
                  <AccordionTrigger className="text-sm text-left hover:no-underline py-3" data-testid={`faq-q-${section.id}-${i}`}>
                    <span><Highlight text={it.q} term={search.trim()} /></span>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-gray-600 leading-relaxed">
                    <Highlight text={it.a} term={search.trim()} />
                    {it.img && (
                      <button
                        type="button"
                        onClick={() => setLightbox({ src: it.img, caption: it.imgCaption || it.q })}
                        className="mt-3 block text-left group"
                        data-testid={`faq-img-${section.id}-${i}`}
                      >
                        <img
                          src={it.img}
                          alt={it.imgCaption || it.q}
                          loading="lazy"
                          className="max-h-56 w-auto max-w-full rounded-md border border-summit-hairline shadow-sm cursor-zoom-in group-hover:shadow-md transition-shadow"
                        />
                        <span className="mt-1.5 flex items-center gap-1.5 text-[11px] text-gray-400 font-mono">
                          <ZoomIn className="w-3 h-3" /> {it.imgCaption || "Click to enlarge"}
                        </span>
                      </button>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </div>

      <div className="mt-8 text-xs text-gray-400 text-center" data-testid="faq-footer">
        Can't find what you need? Post in Team → Discussion or contact your System Admin.
      </div>

      <Dialog open={!!lightbox} onOpenChange={(o) => { if (!o) setLightbox(null); }}>
        <DialogContent className="max-w-4xl p-2 sm:p-4" data-testid="faq-lightbox">
          <DialogTitle className="sr-only">Screenshot</DialogTitle>
          {lightbox && (
            <>
              <img src={lightbox.src} alt={lightbox.caption} className="w-full h-auto rounded-md" />
              <div className="text-xs text-gray-500 text-center pb-1">{lightbox.caption}</div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
